import { useState, useEffect, useRef, useCallback } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import { MapPin, LocateFixed, X, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import env from "@/lib/env";

export interface LocationValue {
  placeId: string;
  displayName: string;
  lat: number;
  lng: number;
}

interface LocationAutocompleteInputProps {
  value?: LocationValue | null;
  onChange: (location: LocationValue | null) => void;
  placeholder?: string;
  className?: string;
  error?: string;
}

let loaderInstance: Loader | null = null;
let autocompleteLibPromise: Promise<google.maps.PlacesLibrary> | null = null;

function getAutocompleteLib(): Promise<google.maps.PlacesLibrary> {
  if (!autocompleteLibPromise) {
    const apiKey = env.get("VITE_GOOGLE_MAPS_API_KEY") || "";
    loaderInstance = new Loader({ apiKey, version: "weekly", libraries: ["places"] });
    autocompleteLibPromise = loaderInstance.importLibrary("places");
  }
  return autocompleteLibPromise;
}

export function LocationAutocompleteInput({
  value,
  onChange,
  placeholder = "Enter your city or area",
  className,
  error,
}: LocationAutocompleteInputProps) {
  const [inputText, setInputText] = useState(value?.displayName || "");
  const [suggestions, setSuggestions] = useState<google.maps.places.AutocompleteSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [geolocating, setGeolocating] = useState(false);
  const [geoError, setGeoError] = useState<string | null>(null);
  const [libLoaded, setLibLoaded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const placesLibRef = useRef<google.maps.PlacesLibrary | null>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load Places library on mount
  useEffect(() => {
    getAutocompleteLib().then((lib) => {
      placesLibRef.current = lib;
      sessionTokenRef.current = new lib.AutocompleteSessionToken();
      setLibLoaded(true);
    }).catch(() => {
      // silently fail — field still usable without autocomplete
    });
  }, []);

  // Sync external value to input text
  useEffect(() => {
    setInputText(value?.displayName || "");
  }, [value?.displayName]);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (!placesLibRef.current || !sessionTokenRef.current || query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const { suggestions: results } = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: query,
        sessionToken: sessionTokenRef.current,
      });
      setSuggestions(results || []);
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputText(val);
    if (!val) {
      onChange(null);
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => fetchSuggestions(val), 300);
  };

  const handleSelectSuggestion = async (suggestion: google.maps.places.AutocompleteSuggestion) => {
    if (!placesLibRef.current) return;
    setShowSuggestions(false);
    const displayName = suggestion.placePrediction?.text.toString() || "";
    setInputText(displayName);

    try {
      const place = suggestion.placePrediction?.toPlace();
      if (!place) return;
      await place.fetchFields({ fields: ["location", "id"] });

      const lat = place.location?.lat() ?? 0;
      const lng = place.location?.lng() ?? 0;
      const placeId = place.id || "";

      onChange({ placeId, displayName, lat, lng });

      // Refresh session token after a selection is complete
      if (placesLibRef.current) {
        sessionTokenRef.current = new placesLibRef.current.AutocompleteSessionToken();
      }
    } catch {
      // Place details fetch failed — still commit with empty coords, server validates
    }
  };

  const handleClear = () => {
    setInputText("");
    setSuggestions([]);
    setShowSuggestions(false);
    onChange(null);
  };

  const handleUseMyLocation = () => {
    if (!navigator.geolocation) {
      setGeoError("Geolocation is not supported by your browser");
      return;
    }
    setGeolocating(true);
    setGeoError(null);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        setGeolocating(false);

        // Reverse geocode to get a display name
        let displayName = `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
        let placeId = `geo:${lat},${lng}`;

        try {
          // Use Geocoder to get a human-readable address
          if (libLoaded && window.google?.maps?.Geocoder) {
            const geocoder = new window.google.maps.Geocoder();
            const result = await geocoder.geocode({ location: { lat, lng } });
            if (result.results.length > 0) {
              const r = result.results[0];
              placeId = r.place_id || placeId;
              // Find city/area name
              const locality = r.address_components?.find(c => c.types.includes("locality"))?.long_name;
              const admin = r.address_components?.find(c => c.types.includes("administrative_area_level_1"))?.long_name;
              const country = r.address_components?.find(c => c.types.includes("country"))?.long_name;
              displayName = [locality, admin, country].filter(Boolean).join(", ") || r.formatted_address || displayName;
            }
          }
        } catch {
          // Fallback to coordinates
        }

        setInputText(displayName);
        onChange({ placeId, displayName, lat, lng });
      },
      (err) => {
        setGeolocating(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGeoError("Location access was denied. Please enable it in your browser settings.");
        } else {
          setGeoError("Could not determine your location. Please type it manually.");
        }
      },
      { timeout: 10000, maximumAge: 60000 }
    );
  };

  return (
    <div className={cn("relative", className)} ref={containerRef}>
      <div
        className={cn(
          "flex items-center gap-2 rounded-md border bg-background px-3 py-2 transition-colors",
          error ? "border-destructive" : "border-input hover:border-secondary/50 focus-within:border-secondary/70",
        )}
      >
        <MapPin className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        <input
          type="text"
          value={inputText}
          onChange={handleInputChange}
          onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
          placeholder={placeholder}
          className="flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
        />
        {inputText && (
          <button type="button" onClick={handleClear} className="flex-shrink-0 text-muted-foreground hover:text-foreground">
            <X className="w-3.5 h-3.5" />
          </button>
        )}
        <button
          type="button"
          onClick={handleUseMyLocation}
          disabled={geolocating}
          className="flex-shrink-0 text-muted-foreground hover:text-secondary transition-colors disabled:opacity-50"
          title="Use my current location"
        >
          {geolocating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LocateFixed className="w-4 h-4" />}
        </button>
      </div>

      {geoError && (
        <p className="text-xs text-destructive mt-1">{geoError}</p>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-xl shadow-2xl z-50 overflow-hidden">
          {suggestions.map((s, i) => {
            const text = s.placePrediction?.text.toString() || "";
            return (
              <button
                key={i}
                type="button"
                onMouseDown={() => handleSelectSuggestion(s)}
                className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50 transition-colors flex items-center gap-2"
              >
                <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                <span className="text-slate-800 truncate">{text}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
